/**
 * Standings calculation — shared between client display and API validation.
 * Supports flexible round/question counts (not hardcoded to 6x5).
 */

export interface RoundScore {
  roundIndex: number;
  questionPoints: number;
  bonusPoints: number;       // single integer bonus (0–N)
  subtotal: number;          // before doubling
  isDoubled: boolean;
  total: number;             // after doubling
}

export interface TeamStanding {
  teamId: string;
  teamName: string;
  playerCount: number;
  roundScores: RoundScore[];
  grandTotal: number;
  rank: number;
}

export interface ScoreRecord {
  teamId: string;
  questionId: string | null;
  roundIndex: number;
  scoreType: 'QUESTION' | 'BONUS' | 'VISUAL' | 'ROUND_TOTAL';
  correct: boolean;
  partialCredit?: boolean;   // half-credit on a QUESTION (awards half of the question value)
  bonusTitleCorrect: boolean;
  bonusArtistCorrect: boolean;
  bonusPoints?: number | null;  // new single-integer bonus field
  visualScore: number | null;
}

export interface TeamRecord {
  id: string;
  name: string;
  playerCount: number;
  doubledRound: number | null;
  leftEarly?: boolean;       // when true, excluded from standings
}

/**
 * Per-round points config. Maps roundIndex (1-based) -> points per
 * fully-correct question. Defaults to 2 if a round isn't in the map.
 * Visual rounds aren't keyed here — their score is the integer in the
 * VISUAL score row's visualScore field, not derived from per-question
 * cells.
 */
export interface PointsConfig {
  [roundIndex: number]: number;
}

/**
 * Own point values of questions: questionId -> points. Only questions that
 * set their own value are keyed here. Every other question uses the round
 * value in PointsConfig.
 */
export interface QuestionPoints {
  [questionId: string]: number;
}

/** Minimal question shape for point values. points null = round default. */
export interface ScoringQuestion {
  id?: string;
  points?: number | null;
}

/** Minimal round shape needed to derive scoring rules. */
export interface ScoringRound {
  orderIndex: number;
  roundType: 'STANDARD' | 'VISUAL' | 'AUDIO';
  pointsPerQuestion?: number | null;
  questions: readonly unknown[];
}

/** The round default value per question. Visual rounds are 1 per image. */
export function roundDefaultPoints(round: Pick<ScoringRound, 'roundType' | 'pointsPerQuestion'>): number {
  return round.roundType === 'VISUAL' ? 1 : (round.pointsPerQuestion ?? 2);
}

/** The value of one question: its own value, or the round default. */
export function questionValue(
  question: ScoringQuestion | undefined,
  round: Pick<ScoringRound, 'roundType' | 'pointsPerQuestion'>,
): number {
  if (round.roundType === 'VISUAL') return 1;
  return question?.points ?? roundDefaultPoints(round);
}

/**
 * Points for one question mark. Full credit awards the question value.
 * Half credit awards half of it, rounded down, like the server
 * (Cerebrex/utils/questionPoints.js).
 */
export function markPoints(mark: 'full' | 'half' | 'wrong' | null | undefined, value: number): number {
  if (mark === 'full') return value;
  if (mark === 'half') return Math.floor(value / 2);
  return 0;
}

/** questionId -> own value, for the questions that set one. */
export function buildQuestionPoints(rounds: readonly ScoringRound[]): QuestionPoints {
  const out: QuestionPoints = {};
  for (const r of rounds) {
    if (r.roundType === 'VISUAL') continue;
    for (const q of r.questions as readonly ScoringQuestion[]) {
      if (q && q.id && q.points != null) out[q.id] = q.points;
    }
  }
  return out;
}

/**
 * Build the roundIndex -> points-per-question map from a quiz's rounds.
 *
 * A visual round is always 1 point per image. round.pointsPerQuestion is
 * non-nullable with @default(2) in the schema, so visual rounds arrive
 * claiming 2 — but calculateStandings never applies it to them (it reads
 * visualScore directly), so honouring it here would only corrupt the
 * denominators that percentage columns and the scoring rail draw against.
 */
export function buildPointsConfig(rounds: readonly ScoringRound[]): PointsConfig {
  const cfg: PointsConfig = {};
  for (const r of rounds) {
    cfg[r.orderIndex + 1] = roundDefaultPoints(r);
  }
  return cfg;
}

/**
 * Clamp a typed visual-round score to 0..items.
 *
 * Visual rounds are one point per image, so the round's image count is the
 * ceiling. Without one a slipped keypress sticks: prod carries a visual
 * score of 46 on an 8-image round, inflating that team's total by ~38.
 * `items` of 0/undefined means "unknown round" — leave the value uncapped
 * rather than clamping everything to zero.
 */
export function clampVisualScore(value: number, items?: number): number {
  return Math.min(Math.max(0, value), items || Infinity);
}

export function calculateStandings(
  teams: TeamRecord[],
  scores: ScoreRecord[],
  pointsConfig: PointsConfig = {},
  ownPoints: QuestionPoints = {},
): TeamStanding[] {
  // Determine all round indices dynamically from the scores. VISUAL
  // scores now use their natural roundIndex (e.g. 6 for a round-6 visual)
  // so they're included alongside QUESTION/BONUS scores — no sentinel.
  const allRoundIndices = new Set<number>();
  for (const s of scores) {
    if (s.roundIndex > 0) {
      allRoundIndices.add(s.roundIndex);
    }
  }
  const sortedRounds = Array.from(allRoundIndices).sort((a, b) => a - b);

  // Teams who left early are filtered out of standings entirely. Their
  // scores remain in the DB and visible to the host, but the leaderboard,
  // ranking, and prize calculations ignore them.
  const activeTeams = teams.filter((t) => !t.leftEarly);

  const standings: TeamStanding[] = activeTeams.map((team) => {
    const teamScores = scores.filter((s) => s.teamId === team.id);
    const roundScores: RoundScore[] = [];

    for (const r of sortedRounds) {
      // VISUAL rounds (and ROUND_TOTAL historical imports) collapse to a
      // single integer (visualScore). When a round has such a row, treat
      // that as the round's questionPoints — no per-question cells, no
      // bonus — and skip the standard QUESTION/BONUS accumulation.
      const visual = teamScores.find(
        (s) => s.roundIndex === r && (s.scoreType === 'VISUAL' || s.scoreType === 'ROUND_TOTAL'),
      );
      if (visual) {
        const subtotal = visual.visualScore ?? 0;
        const isDoubled = team.doubledRound === r;
        roundScores.push({
          roundIndex: r,
          questionPoints: subtotal,
          bonusPoints: 0,
          subtotal,
          isDoubled,
          total: isDoubled ? subtotal * 2 : subtotal,
        });
        continue;
      }

      // Points-per-question for this round. Defaults to 1 when the caller
      // omits pointsConfig (preserves backward compat with the old
      // one-point-per-question scoring). Real callers should pass the
      // event's per-round config so STANDARD rounds get 2 pts/q.
      const ppq = pointsConfig[r] ?? 1;
      // A question with its own value (ownPoints) uses that value.
      // Half credit awards half of the value, rounded down.
      let questionPoints = 0;
      for (const s of teamScores) {
        if (s.roundIndex !== r || s.scoreType !== 'QUESTION') continue;
        const value = (s.questionId != null ? ownPoints[s.questionId] : undefined) ?? ppq;
        questionPoints += markPoints(s.correct ? 'full' : s.partialCredit ? 'half' : null, value);
      }

      const bonus = teamScores.find(
        (s) => s.roundIndex === r && s.scoreType === 'BONUS',
      );

      // Use bonusPoints field if present, otherwise fall back to title+artist booleans
      let bonusPoints: number;
      if (bonus?.bonusPoints != null) {
        bonusPoints = bonus.bonusPoints;
      } else {
        bonusPoints =
          (bonus?.bonusTitleCorrect ? 1 : 0) +
          (bonus?.bonusArtistCorrect ? 1 : 0);
      }

      const subtotal = questionPoints + bonusPoints;
      const isDoubled = team.doubledRound === r;
      const total = isDoubled ? subtotal * 2 : subtotal;

      roundScores.push({
        roundIndex: r,
        questionPoints,
        bonusPoints,
        subtotal,
        isDoubled,
        total,
      });
    }

    const grandTotal = roundScores.reduce((sum, r) => sum + r.total, 0);

    return {
      teamId: team.id,
      teamName: team.name,
      playerCount: team.playerCount,
      roundScores,
      grandTotal,
      rank: 0, // assigned below
    };
  });

  // Sort by grandTotal descending, assign ranks with ties
  standings.sort((a, b) => b.grandTotal - a.grandTotal);
  let currentRank = 1;
  for (let i = 0; i < standings.length; i++) {
    const current = standings[i]!;
    if (i > 0 && current.grandTotal < standings[i - 1]!.grandTotal) {
      currentRank = i + 1;
    }
    current.rank = currentRank;
  }

  return standings;
}
